import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon from '@op/components/BubbleCommon';
import ChannelLabel from '@op/components/ChannelLabel';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import UserInfoItem from '@op/components/Snippets/UserInfoItem';
import PopGameAffinity from './../../GenreCreatorList/PopGameAffinity';
import {
  arrayToSimpleObj,
  getPercentageInfo,
  getChartsShowCount,
  getRationPercentValue,
} from '@op/utils';
import { openWindowUrl } from '@op/utils/url';
import { specialSecimalFormatPercentage, reviewsFormat } from '@op/utils/value';
import moment from 'moment';
import React from 'react';
import { List, Badge, Bubble } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { maxValueKeyMap } from '../../constants';

export function ChartTableElement({
  item,
  valueKey,
  title,
  linkTime,
  chartMaxValue,
  showRatio = true,
}: {
  item: any;
  valueKey: string;
  title: string;
  linkTime: {
    new?: { start_time: string; end_time: string };
    old?: { start_time: string; end_time: string };
  };
  chartMaxValue: { [key: string]: number };
  showRatio?: boolean;
}) {
  const { start_time, end_time } = linkTime?.new || {};
  const { start_time: start_time_old, end_time: end_time_old } =
    linkTime?.old || {};

  const { formatMessage } = useIntl();
  const data = item[valueKey];

  if (
    !data ||
    !Array.isArray(data) ||
    data.length === 0 ||
    data.every((item) => item.value === 0)
  )
    return '-';

  const { total, text, video, image } = arrayToSimpleObj(
    data,
    'value',
    (item) => {
      return item.name?.split?.('_')?.[0] || item.name;
    },
  );

  const allTotal = chartMaxValue[maxValueKeyMap[valueKey]] || 0;

  const textPercentage = getPercentageInfo(text || null, allTotal);
  const videoPercentage = getPercentageInfo(video || null, allTotal);
  const imagePercentage = getPercentageInfo(image || null, allTotal);

  const oldData = item['old_' + valueKey] || [];
  let oldTotal = 0;
  if (oldData) {
    const { value: oldLocalTotal } =
      oldData.find(
        (item: { name: string }) => item.name === 'total_' + valueKey,
      ) || {};
    oldTotal = oldLocalTotal || 0;
  }

  const list = [
    {
      text: formatMessage({ id: 'Video' }),
      percentage: video ? getPercentageInfo(video, total) : null,
      color: '#4f86f3',
      value: video,
      barPercentage: videoPercentage,
    },
    {
      text: formatMessage({ id: 'Text' }),
      percentage: text ? getPercentageInfo(text, total) : null,
      color: '#69d19c',
      value: text,
      barPercentage: textPercentage,
    },
    {
      text: formatMessage({ id: 'Image' }),
      percentage: image ? getPercentageInfo(image, total) : null,
      color: '#F8D74D',
      value: image,
      barPercentage: imagePercentage,
    },
  ];

  const element = (
    <div className="dbo-chart-tips-wrapper">
      <div className="dbo-title">{formatMessage({ id: title })}</div>
      <List className="list-box">
        {[
          ...list,
          {
            text: formatMessage({ id: 'Total' }),
            value: total,
            color: undefined,
            percentage: null,
          },
        ].map(({ text, value, color, percentage }) => {
          return (
            <List.Item key={text} className="list-item">
              <label className="text-info">
                {color && (
                  <Badge
                    className="badge-item"
                    dot
                    style={{ backgroundColor: color }}
                  />
                )}
                {text}
              </label>
              <label className="value-info">
                {reviewsFormat(value)}
                {percentage === null ? '' : `(${percentage})`}
              </label>
            </List.Item>
          );
        })}
      </List>
    </div>
  );

  return (
    <div className="chart-table-element-box">
      <div>
        {/* <span className="total-info">
          {formatMessage({ id: 'Total' })}: {reviewsFormat(total)}
        </span> */}
        <PercentageItem
          className="dbo-numtrends"
          percentage={getRationPercentValue(total, oldTotal)}
        >
          {(percentageElement: any) => (
            <>
              <span className="dbo-numtrends-num">
                {formatMessage({ id: 'Total' })}: {reviewsFormat(total, 2, '-')}
              </span>
              {showRatio && (
                <RingRatioBubble
                  oldTime={[start_time_old, end_time_old]}
                  newTime={[start_time, end_time]}
                  oldValue={oldTotal}
                  newValue={total}
                  title={formatMessage({ id: title })}
                  formateDate={(key: moment.MomentInput) =>
                    !key ? '-' : moment(key).format('YYYY-MM-DD')
                  }
                  formateValue={(key: number) => reviewsFormat(key, 2, '-')}
                >
                  {percentageElement}
                </RingRatioBubble>
              )}
            </>
          )}
        </PercentageItem>
      </div>
      <Bubble dark closeOnScroll content={element}>
        <div className="percentage-info">
          {list.map(({ barPercentage, color, value }) => {
            const minWidth = value && barPercentage === '0%' ? '1px' : '';
            return (
              <span
                key={color}
                className="percentage-item"
                style={{
                  width: minWidth || barPercentage,
                  backgroundColor: color,
                }}
              ></span>
            );
          })}
        </div>
      </Bubble>
    </div>
  );
}

export function ValueTableElement({
  item,
  valueKey,
  title,
  linkTime = {},
  isPercentage = false,
  progressVisible,
  showRatio = true,
}: {
  item: any;
  valueKey: string;
  title: string;
  isPercentage?: boolean;
  linkTime: {
    new?: { start_time: string; end_time: string };
    old?: { start_time: string; end_time: string };
  };
  progressVisible: boolean;
  showRatio?: boolean;
}) {
  const { formatMessage } = useIntl();

  const value = item?.[valueKey] ?? 0;
  const percentage = item?.[`${valueKey}_than`] ?? 0;
  const barPercentage = item?.[`${valueKey}_progress`] ?? 0;
  const old_value = item?.[`old_${valueKey}`] ?? 0;

  const { start_time, end_time } = linkTime?.new || {};
  const { start_time: start_time_old, end_time: end_time_old } =
    linkTime?.old || {};

  const formatPercentValue = (value: number) => {
    if (valueKey === 'view_impairment_rate' && value === -99999) {
      return '-';
    }
    const defaultValue = valueKey === 'view_impairment_rate' ? '0.00%' : '-';
    return specialSecimalFormatPercentage(value, defaultValue);
  };

  return (
    <div className={`${progressVisible ? 'is-percent' : 'is-no-percent'}`}>
      <div className="table-text-bg" style={{ width: barPercentage }}></div>

      <PercentageItem className="dbo-numtrends" percentage={percentage}>
        {(percentageElement: any) => (
          <>
            <span className="dbo-numtrends-num">
              {isPercentage
                ? formatPercentValue(value)
                : getChartsShowCount(value, 2, '-')}
            </span>
            {showRatio && (
              <RingRatioBubble
                oldTime={[start_time_old, end_time_old]}
                newTime={[start_time, end_time]}
                oldValue={old_value}
                newValue={value}
                title={formatMessage({ id: title })}
                formateDate={(key: moment.MomentInput) =>
                  !key ? '-' : moment(key).format('YYYY-MM-DD')
                }
                formateValue={(key: number) =>
                  isPercentage
                    ? specialSecimalFormatPercentage(key)
                    : getChartsShowCount(key, 2, '-')
                }
              >
                {percentageElement}
              </RingRatioBubble>
            )}
          </>
        )}
      </PercentageItem>
    </div>
  );
}

export function GameCoveredElement({ data = [] }: any) {
  if (!Array.isArray(data)) return null;
  data.sort((a, b) => b.affinity - a.affinity);
  const isShort = data.length < 5;
  const renderList = isShort ? data : data.slice(0, 3);
  const moreNumber = isShort ? 0 : data.length - 3;
  return (
    <Bubble
      placement="top"
      content={<PopGameAffinity data={data}></PopGameAffinity>}
      className="bubble-nonestyle"
    >
      <div className="game-covered-element-box">
        {renderList.map((item: any) => {
          return (
            <RenderGameIcon
              className="game-icon-item"
              gameInfo={{
                entity_type: 'mobile',
                cover: item.cover,
                edition_unified_id: item.game_id,
              }}
              key={item.game_id}
            ></RenderGameIcon>
          );
        })}
        {moreNumber > 0 && (
          <div className="game-icon-item more">+{moreNumber}</div>
        )}
      </div>
    </Bubble>
  );
}

export const getInfluencerElement: any = function ({
  formatMessage,
  isAuto,
  jumpToFeeds,
  transRegion,
  influencerTips,
  valueKeyList = [
    { key: 'avatar' },
    { key: 'user_id' },
    { key: 'name' },
    { key: 'url' },
    { key: 'channel' },
    { key: 'language' },
    { key: 'country' },
  ],
}: {
  formatMessage: any;
  isAuto: any;
  jumpToFeeds?: any;
  transRegion: any;
  influencerTips: any;
  valueKeyList: { key: string; valueKey?: string }[];
}) {
  function getValue(data: any) {
    return valueKeyList.reduce((pre: any, { key, valueKey }: any) => {
      const realKey = valueKey || key;
      let value = data[realKey];
      return {
        ...pre,
        [key]: value,
      };
    }, {});
  }

  return {
    key: 'influencer',
    header: (
      <BubbleCommon showIcon={false} tipsMap={influencerTips}>
        <div className="dbo-influencer-table-header">
          {formatMessage({ id: 'Influencers-1' })}
        </div>
      </BubbleCommon>
    ),
    fixed: 'left',
    width: isAuto ? 'auto' : 230,
    render: (data: any) => {
      const { user_id, name, avatar, channel, country, language, url } =
        getValue(data);
      return (
        <div className="influencer-info-item">
          <UserInfoItem
            showBubble
            onlyOneLine
            isLink={false}
            shareUrl="-"
            icon={avatar}
            name={name || user_id}
            shareUrlElement={
              <div className="icon-box">
                <Bubble
                  closeOnScroll
                  content={formatMessage({ id: "Go to influencer's homepage" })}
                  dark
                >
                  <SvgIcon
                    type="other-share-icon"
                    onClick={() => openWindowUrl(url)}
                  />{' '}
                </Bubble>
                {jumpToFeeds && (
                  <Bubble
                    closeOnScroll
                    content={formatMessage({ id: 'View details' })}
                    dark
                  >
                    <SvgIcon
                      type="view-details-icon"
                      onClick={() => jumpToFeeds(user_id)}
                    />{' '}
                  </Bubble>
                )}
              </div>
            }
          >
            {(className: string | undefined, classNamePrefix: string) => (
              <div className={className}>
                <div className={`${classNamePrefix}-channel`}>
                  <ChannelLabel channel_name={channel} showText={false} />
                </div>
                <div className={`${classNamePrefix}-language`}>
                  {country && (
                    <div className="dbo-common-country-line">
                      <RegionCountryItem showText={false} market={country} />
                    </div>
                  )}
                  {country && language && (
                    <div className="dbo-common-divider">|</div>
                  )}
                  {language &&
                    transRegion(language, getLocale() === 'zh-CN' ? 6 : 3)}
                </div>
              </div>
            )}
          </UserInfoItem>
        </div>
      );
    },
  };
};
